/**
 * WORKFLOW STEPS DISPLAY COMPONENT
 *
 * A React component that visualizes AI-generated CSV transformation workflows
 * in real-time. Supports both traditional and orchestrator-worker pattern workflows.
 *
 * FEATURES:
 * - Real-time workflow step visualization during AI generation
 * - Admin/debug mode with detailed inputs/outputs inspection
 * - Loading states and progress indicators
 * - Tooltips for technical operation explanations
 * - Visual distinction between workflow phases (preprocessing vs transformation)
 * - Responsive design with card-based layout
 *
 * WORKFLOW OPERATION TYPES:
 * - extract_table_bounds: Remove metadata, focus on data table
 * - delete_rows: Remove summary/total rows by OriginalRowNumber
 * - delete_columns: Remove unnecessary columns by name/index
 * - construct_headers: Combine multi-row headers into meaningful names
 * - reverse_pivot: Convert wide format to long format (hierarchical support)
 *
 * ORCHESTRATOR-WORKER PATTERN SUPPORT:
 * - Shows orchestrator strategy planning phase
 * - Displays parallel worker execution with retry indicators
 * - Real-time success/failure feedback for each worker
 * - Performance metrics (execution time, success rates)
 */

import React, { useState, useContext } from 'react';
import {
  CheckIcon,
  QuestionIcon,
  EyeIcon,
  EyeSlashIcon,
  CopyIcon,
  WarningCircleIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { AuthContext } from '@/context/auth.context';
import { useViewMode } from '@/context/view-mode.context';
import { isAdmin } from '@/utils/user';

/**
 * Execution status for workflow steps
 */
export type WorkflowStepStatus = 'success' | 'failed' | 'not_executed';

/**
 * Represents a single workflow operation step
 */
export interface WorkflowStep {
  /** Unique identifier for the step (e.g., 'step_1') */
  id?: string;
  /** Operation type (e.g., 'extract_table_bounds', 'delete_rows') */
  type: string;
  /** Human-readable description of what this step does */
  description: string;
  /** Input parameters for the operation (hidden in user mode) */
  inputs?: unknown;
  /** Output results from the operation (columns created, etc.) */
  outputs?: {
    columns?: string[];
    [key: string]: unknown;
  };
  /** Execution status: success, failed, or not_executed */
  status?: WorkflowStepStatus;
  /** Error message if status is "failed" */
  error?: string;
}

/**
 * Props for the WorkflowStepsDisplay component
 */
export interface WorkflowStepsDisplayProps {
  /** Processed workflow steps for user display (sanitized inputs) */
  workflowSteps?: WorkflowStep[];
  /** Raw workflow steps with full technical inputs (admin/debug mode) */
  rawWorkflowSteps?: WorkflowStep[];
  /** Whether the AI is currently generating workflow steps */
  isGenerating?: boolean;
  /** Custom title for the workflow section */
  title?: string;
  /** Tooltip text explaining the workflow purpose */
  tooltip?: string;
  /** Additional CSS classes for styling */
  className?: string;
  /** Workflow phase for visual distinction and context */
  phase?: 'preprocessing' | 'transformation';
  /** Overall workflow error message if execution failed */
  workflowError?: string;
  /** ID of the step that failed (if any) */
  failedStepId?: string;
}

/**
 * WORKFLOW STEPS DISPLAY COMPONENT
 *
 * A reusable React component for visualizing AI-generated CSV transformation workflows.
 * Provides real-time feedback during workflow generation and execution.
 *
 * USAGE CONTEXTS:
 * - Phase 1 (Preprocessing): Shows structural transformations (pivot, headers, cleanup)
 * - Phase 3 (Transformation): Shows business logic operations (column splitting, mapping)
 * - Admin Mode: Exposes technical inputs/outputs for debugging
 * - User Mode: Shows friendly descriptions and progress indicators
 *
 * ORCHESTRATOR-WORKER PATTERN INTEGRATION:
 * - Displays orchestrator strategy planning phase
 * - Shows parallel worker execution with success/retry indicators
 * - Real-time updates as workers complete their tasks
 * - Performance metrics and execution summaries
 *
 * @param workflowSteps Sanitized steps for user display
 * @param rawWorkflowSteps Complete technical steps for admin/debug mode
 * @param isGenerating Loading state during AI workflow generation
 * @param title Custom section title
 * @param tooltip Explanatory text for the workflow purpose
 * @param className Additional styling classes
 * @param phase Workflow phase for context and visual styling
 */
export function WorkflowStepsDisplay({
  workflowSteps,
  rawWorkflowSteps,
  isGenerating = false,
  title = 'Workflow Steps',
  tooltip = 'These are the CSV operations used to transform your data into the correct format. Each step represents a specific transformation applied to your spreadsheet data.',
  className = '',
  phase = 'transformation',
  workflowError,
  failedStepId,
}: WorkflowStepsDisplayProps) {
  const [showRawSteps, setShowRawSteps] = useState(false);

  // Admin check for debug button visibility
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const showDebugButton = isAdmin(currentUser, viewMode);

  // Copy debug text to clipboard
  const copyDebugTextToClipboard = async () => {
    if (!rawWorkflowSteps || rawWorkflowSteps.length === 0) return;

    const debugTexts = rawWorkflowSteps.map((step, index) => {
      const stepId = step.id || `step-${index}`;
      return `Step ${index + 1} (${stepId}): ${JSON.stringify(step, null, 2)}`;
    });

    const combinedText = debugTexts.join(',\n\n');

    try {
      await navigator.clipboard.writeText(combinedText);
    } catch (err) {
      console.error('Failed to copy debug text:', err);
    }
  };
  // Don't render if no steps and not generating
  if (!isGenerating && (!workflowSteps || workflowSteps.length === 0)) {
    return null;
  }

  const phaseLabel = phase === 'preprocessing' ? 'Prep' : 'Step';

  return (
    <div className={`mb-8 ${className}`}>
      <div className="flex items-center gap-2 mb-4">
        <h3 className="text-lg font-semibold">{title}</h3>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <QuestionIcon className="h-4 w-4 text-gray-500 hover:text-gray-700 cursor-help" />
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-xs">{tooltip}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {showDebugButton && rawWorkflowSteps && rawWorkflowSteps.length > 0 && (
          <>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setShowRawSteps(!showRawSteps)}
                    className="h-4 w-4 text-gray-500 hover:text-gray-700 cursor-pointer"
                  >
                    {showRawSteps ? <EyeSlashIcon /> : <EyeIcon />}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {showRawSteps
                      ? 'Hide raw workflow inputs'
                      : 'Show raw workflow inputs'}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={copyDebugTextToClipboard}
                    className="h-4 w-4 text-gray-500 hover:text-gray-700 cursor-pointer"
                  >
                    <CopyIcon />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Copy debug text for all steps to clipboard</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </>
        )}
      </div>

      {/* Workflow Error Display */}
      {workflowError && (
        <div className="mb-6">
          <Card className="border-red-200 bg-red-50">
            <CardContent>
              <div className="flex items-start text-red-600">
                <WarningCircleIcon className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold">Workflow Execution Failed</h4>
                  <p className="text-sm">{workflowError}</p>
                  {failedStepId && (
                    <p className="text-xs text-red-500 mt-1">
                      Failed at step: {failedStepId}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardContent>
          {isGenerating ? (
            <LoadingSpinner
              text={`Generating ${phase} workflow...`}
              minHeight={120}
              size={48}
            />
          ) : (
            workflowSteps?.map((step: WorkflowStep, index: number) => {
              const stepId = step.id || `step-${index}`;
              const rawStep = rawWorkflowSteps?.find(
                (raw) =>
                  raw.id === step.id || index === rawWorkflowSteps.indexOf(raw),
              );
              // Simple: trust the status from the backend
              const stepStatus = step.status || 'success'; // Default to success for backward compatibility

              return (
                <div
                  key={stepId}
                  className={`${
                    index < workflowSteps.length - 1 ? 'mb-6' : ''
                  }`}
                >
                  <div className="flex items-center">
                    <Badge
                      variant={
                        stepStatus === 'failed'
                          ? 'destructive'
                          : stepStatus === 'not_executed'
                            ? 'secondary'
                            : 'default'
                      }
                      className={`mr-4 min-w-[80px] ${
                        stepStatus === 'failed'
                          ? 'bg-red-100 text-red-800'
                          : stepStatus === 'not_executed'
                            ? 'bg-gray-100 text-gray-600'
                            : phase === 'preprocessing'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-green-100 text-green-800'
                      }`}
                    >
                      {stepStatus === 'failed' ? (
                        <XIcon className="w-3 h-3 mr-1" />
                      ) : stepStatus === 'not_executed' ? (
                        <WarningCircleIcon className="w-3 h-3 mr-1" />
                      ) : (
                        <CheckIcon className="w-3 h-3 mr-1" />
                      )}
                      {phaseLabel} {index + 1}
                    </Badge>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium">
                        {formatOperationType(step.type)}
                      </h4>
                      <p
                        className={`text-sm ${
                          stepStatus === 'not_executed'
                            ? 'text-gray-400'
                            : 'text-gray-600'
                        }`}
                      >
                        {stepStatus === 'not_executed'
                          ? 'Not executed (skipped due to earlier failure)'
                          : step.description}
                      </p>
                      {step.outputs?.columns &&
                        stepStatus !== 'not_executed' && (
                          <p className="text-xs text-gray-500 mt-1">
                            Columns: {step.outputs.columns.join(', ')}
                          </p>
                        )}
                      {/* Show error details for failed step */}
                      {stepStatus === 'failed' && (
                        <div className="text-red-600 text-sm mt-1">
                          <strong>Error:</strong> {step.error || workflowError}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Individual Step Debug Info - shown when global toggle is on */}
                  {showRawSteps && rawStep && (
                    <div className="mt-3 ml-24 mr-4">
                      <div className="bg-gray-50 p-3 rounded border">
                        <h5 className="text-xs font-semibold text-gray-700 mb-2">
                          Debug Info for {formatOperationType(step.type)}
                        </h5>
                        <pre className="text-xs text-gray-800 overflow-x-auto">
                          {JSON.stringify(rawStep, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Format operation type for better display
 */
function formatOperationType(type: string): string {
  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export default WorkflowStepsDisplay;
